import { currencyCodeToSymbol, type PriceView } from '@/types';
import { logger } from '@/utils/logger/logger';
import {
  CURRENCY_SYMBOLS_AFTER_AMOUNT,
  ZERO_DECIMAL_CURRENCIES,
} from '@heidi/commerce/contracts';
import {
  useQueryProducts as useQueryProductsCore,
  useUserBillingInfo,
  type UseQueryProductsQueryParams as CoreUseQueryProductsQueryParams,
  type ProductCatalogueVersion,
} from '@heidi/commerce/data';
import {
  BillingSeatType,
  HeidiVersion,
  ProductType,
  SubscriptionTier,
} from '@heidi/schema/ml-scribe';
import { addDays, format } from 'date-fns';
import { isEmpty } from 'lodash-es';
import { useIsUserRegionSameAsKindeRegion } from '../useIsUserRegionSameAsKindeRegion';
import { useQueryUser } from './useQueryUser';

// Re-export GET_PRODUCTS here: a bare `export { X } from` does not bind the name locally.
import {
  GET_PRODUCTS,
  Period,
  type AddOnProductView,
  type CatalogueProductView,
  type ProductView,
} from '@heidi/commerce/contracts';

export { GET_PRODUCTS };

export const getCurrencySymbol = (currencyCode: string): string => {
  return currencyCodeToSymbol[currencyCode.toUpperCase()] || '$';
};

export const isZeroDecimalCurrency = (currencyCode: string): boolean => {
  return ZERO_DECIMAL_CURRENCIES.includes(currencyCode.toUpperCase());
};

export const shouldCurrencySymbolComeAfter = (currencyCode: string): boolean => {
  return CURRENCY_SYMBOLS_AFTER_AMOUNT.has(currencyCode.toUpperCase());
};

export const formatPriceWithCurrency = (
  price: string,
  currencyCode: string,
  currencySymbol: string
): string => {
  if (shouldCurrencySymbolComeAfter(currencyCode)) {
    return `${price} ${currencySymbol}`;
  }
  return `${currencySymbol}${price}`;
};

export type UseQueryProductsQueryParams = CoreUseQueryProductsQueryParams & {
  /**
   * Opt this call site into the v3 catalogue for a `heidi_version === 2`
   * member. Off by default: a surface whose product lookups only understand
   * v2 rows (no `seat_type`) must keep reading v2.
   */
  heidi2Catalogue?: boolean;
};

/** Delegate for `@heidi/commerce/data`. Pricing helpers stay — they read `@heidi/commerce/contracts`. */
export function useQueryProducts(queryParams?: UseQueryProductsQueryParams) {
  const { data: user } = useQueryUser();
  const { userRegionSameAsKindeRegion } = useIsUserRegionSameAsKindeRegion();
  const {
    data: billingInfo,
    isPending: isBillingPending,
    isError: isBillingError,
  } = useUserBillingInfo();
  const { heidi2Catalogue = false, ...coreQueryParams } = queryParams ?? {};
  const heidiVersion = billingInfo?.heidi_version;
  const waitingForHeidiVersion =
    heidi2Catalogue && heidiVersion === undefined && (isBillingPending || isBillingError);
  const catalogueVersion: ProductCatalogueVersion =
    heidi2Catalogue && heidiVersion === HeidiVersion.Value2 ? 'v3' : 'v2';

  const result = useQueryProductsCore(
    {
      // Unresolved Heidi version: disable so v2 prices don't flash.
      hasUser: !!user && !waitingForHeidiVersion,
      userRegionSameAsKindeRegion,
      catalogueVersion,
    },
    coreQueryParams
  );

  // `enabled: false` still returns warm `[GET_PRODUCTS, 'v2']` cache. Strip it
  // while version is unresolved. Billing error stays blank — no v2 fallback.
  if (waitingForHeidiVersion) {
    return {
      ...result,
      data: undefined,
      isPending: !isBillingError,
      isError: isBillingError || result.isError,
      catalogueVersion,
    };
  }

  return { ...result, catalogueVersion };
}

const DEFAULT_DISCOUNT = 28;
export const calculateYearlyDiscount = (products?: ProductView[]) => {
  if (!products) {
    return DEFAULT_DISCOUNT;
  }

  const proProduct = products?.find((prod) => prod.tier === SubscriptionTier.PRO);

  if (!proProduct || isEmpty(proProduct.prices)) {
    return DEFAULT_DISCOUNT;
  }

  const monthlyProProduct = getPlanFromPeriod(Period.ONE_MONTH, proProduct);
  const yearlyProProduct = getPlanFromPeriod(Period.ONE_YEAR, proProduct);

  if (!monthlyProProduct || !yearlyProProduct) {
    return DEFAULT_DISCOUNT;
  }

  const monthlyPricesInAYear = (monthlyProProduct.unit_amount || 0) * 12;
  const yearlyTotalPrice = yearlyProProduct.unit_amount || 0;

  const yearlyDiscountInPercentage = Math.ceil(
    ((monthlyPricesInAYear - yearlyTotalPrice) / monthlyPricesInAYear) * 100
  );

  return yearlyDiscountInPercentage;
};

const DEFAULT_DISCOUNT_MONTHS = 3;
export const calculateYearlyDiscountInMonths = (product?: ProductView) => {
  const yearlyPrice = getPlanFromPeriod(Period.ONE_YEAR, product);
  const monthlyPrice = getPlanFromPeriod(Period.ONE_MONTH, product);

  if (!yearlyPrice || !monthlyPrice) {
    return DEFAULT_DISCOUNT_MONTHS;
  }

  return Math.floor(
    (monthlyPrice.unit_amount * 12 - yearlyPrice.unit_amount) / monthlyPrice.unit_amount
  );
};

export const getPlanFromPeriod = (
  period: Period,
  products?: ProductView | AddOnProductView
) => {
  if (!products) {
    return;
  }
  return products?.prices.find((product) => product.period === period);
};

export const getTierProduct = (
  tier: SubscriptionTier | undefined,
  products?: ProductView[]
) => {
  if (!products || !tier) {
    return;
  }
  const product = products.find(
    (product) => product.tier === tier && product.type === ProductType.scribe
  );
  return product;
};

/** Row for a tier + seat. Missing `seat_type` (v2) reads as STANDARD. */
export const getNewProductTierProduct = (
  tier: SubscriptionTier,
  products?: CatalogueProductView[],
  seatType: BillingSeatType = BillingSeatType.STANDARD
) => {
  if (!products) {
    return;
  }
  return products.find(
    (product) =>
      product.new_product_tier === tier &&
      (product.seat_type ?? BillingSeatType.STANDARD) === seatType
  );
};

export const getProductByName = (products: ProductView[] | undefined, name: string) => {
  if (!products) {
    return;
  }
  return products.find((product) => product.name === name);
};

export const getProductUpgradeDisplayPrices = (
  product: ProductView | undefined,
  locale?: string
) => {
  const monthlyPlan = getPlanFromPeriod(Period.ONE_MONTH, product);
  const yearlyPlan = getPlanFromPeriod(Period.ONE_YEAR, product);

  const monthlyPrice = getDisplayedPriceFromPlan(monthlyPlan, 'month', false, locale);
  const annualPrice = getDisplayedPriceFromPlan(yearlyPlan, 'month', false, locale);

  let annualSavePercent: number | undefined;
  if (monthlyPlan?.unit_amount && yearlyPlan?.unit_amount) {
    const monthlyPricesInAYear = monthlyPlan.unit_amount * 12;
    annualSavePercent = Math.ceil(
      ((monthlyPricesInAYear - yearlyPlan.unit_amount) / monthlyPricesInAYear) * 100
    );
  }

  return { monthlyPrice, annualPrice, annualSavePercent };
};

/**
 * Get the decimal separator for the given locale
 */
const getDecimalSeparator = (locale?: string): string => {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
    return parts.find((part) => part.type === 'decimal')?.value || '.';
  } catch (e) {
    logger.warn('Intl.NumberFormat failed for locale:', locale, e);
    return '.';
  }
};

/**
 * Format a number with locale-aware decimal separator
 */
const formatPriceNumber = (
  value: number,
  locale?: string,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {}
): string => {
  const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
  try {
    return value.toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping: false,
    });
  } catch (e) {
    logger.warn('toLocaleString failed for locale:', locale, e);
    return value.toFixed(maximumFractionDigits);
  }
};

export const getDisplayedPriceFromPlan = (
  plan?: PriceView,
  planDuration: 'month' | 'year' = 'month',
  showDuration = true,
  locale?: string,
  /**
   * Optional translated duration text for localization.
   * If not provided, falls back to English defaults ('mo', 'yr').
   */
  durationText?: {
    monthShort?: string;
    yearShort?: string;
  }
) => {
  if (!plan) {
    return;
  }
  const currencySymbol = getCurrencySymbol(plan.currency.toUpperCase());
  const isZeroDecimal = isZeroDecimalCurrency(plan.currency);
  const divisor = isZeroDecimal ? 1 : 100;

  let price: number;

  if (plan.period === Period.ONE_MONTH) {
    price = plan.unit_amount / divisor;
    if (planDuration === 'year') {
      price = price * 12;
    }
  } else if (plan.period === Period.ONE_YEAR) {
    if (planDuration === 'month') {
      price = plan.unit_amount / divisor / 12;
    } else {
      price = plan.unit_amount / divisor;
    }
  } else {
    price = plan.unit_amount / divisor;
  }

  let formattedPrice: string;
  if (isZeroDecimal) {
    try {
      formattedPrice = price.toLocaleString(locale, {
        maximumFractionDigits: 0,
        useGrouping: true,
      });
    } catch (e) {
      logger.warn('toLocaleString failed for locale:', locale, e);
      formattedPrice = Math.round(price)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  } else {
    const decimalSeparator = getDecimalSeparator(locale);
    formattedPrice = formatPriceNumber(price, locale);
    // Remove trailing zeros for whole numbers (e.g., "50.00" -> "50" or "50,00" -> "50")
    if (formattedPrice.endsWith(`${decimalSeparator}00`)) {
      formattedPrice = formattedPrice.slice(0, -3);
    }
  }

  const priceWithCurrency = formatPriceWithCurrency(
    formattedPrice,
    plan.currency,
    currencySymbol
  );

  if (!showDuration) {
    return priceWithCurrency;
  }

  const durationLabel =
    planDuration === 'month'
      ? (durationText?.monthShort ?? 'mo')
      : (durationText?.yearShort ?? 'yr');

  return `${priceWithCurrency} ${plan.currency.toUpperCase()}/${durationLabel}`;
};

export const getChargingDate = ({
  trialLength,
  shouldShowTrial,
}: {
  trialLength: number | undefined;
  shouldShowTrial: boolean | undefined;
}) => {
  const daysUntilCharge = shouldShowTrial ? trialLength || 7 : 7;

  const trialDate = addDays(new Date(), daysUntilCharge);

  return format(trialDate, 'dd/MM/yy');
};

export const roundDownToNearestFive = (num: number | null | undefined) => {
  if (!num) return 0;
  return Math.floor(num / 5) * 5;
};
